import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PlayheadLine,
  PlayheadTimeLabel,
  RulerLabel,
  RulerTick,
  SceneBlock,
  SceneBlockLabel,
  SceneBlockTime,
  SceneTrack,
  ScrubBar,
  ScrubFill,
  ScrubKnob,
  TimelineArea,
  TimelineHeader,
  TimelineInner,
  TimelineScroll,
  TimeRuler,
} from "../style";
import { Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import PauseRounded from "@mui/icons-material/PauseRounded";
import ContentCutRoundedIcon from "@mui/icons-material/ContentCutRounded";
import MergeTypeRoundedIcon from "@mui/icons-material/MergeTypeRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ContentPasteRoundedIcon from "@mui/icons-material/ContentPasteRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import RedoRoundedIcon from "@mui/icons-material/RedoRounded";
import useLanguage from "../../../hooks/useLanguage";

const TIMELINE_LEFT_PAD = 16;

const STRATEGY_COLORS = {
  focus: "#9b46e1",
  static: "#2563eb",
  bouncing: "#d97706",
  virtual_scene: "#16a34a",
  undefined: "#6b7280",
  fallback: "#dc2626",
};

const STRATEGY_COLORS_DARK = {
  focus: "#5b21b6",
  static: "#1e3a8a",
  bouncing: "#78350f",
  virtual_scene: "#14532d",
  undefined: "#1f2937",
  fallback: "#7f1d1d",
};

const MIN_BLOCK_PX = 60;
const TIMELINE_PX_PER_SEC = 80;

function formatTime(secs) {
  if (!isFinite(secs) || secs == null || secs < 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function buildRulerTicks(dur) {
  if (!dur || !isFinite(dur) || dur <= 0) return [];
  let majorStep = 5;
  if (dur > 600) majorStep = 60;
  else if (dur > 300) majorStep = 30;
  else if (dur > 120) majorStep = 15;
  else if (dur > 60) majorStep = 10;
  const minorStep = majorStep / 5;
  const majorCount = Math.round(majorStep / minorStep);
  const ticks = [];
  let step = 0;
  for (let t = 0; t <= dur + minorStep * 0.01; t += minorStep, step++) {
    if (t > dur) break;
    ticks.push({ t, px: t * TIMELINE_PX_PER_SEC, major: step % majorCount === 0 });
  }
  return ticks;
}

// True when indices form an unbroken integer range (e.g. {3,4,5}).
function isContiguous(indices) {
  if (!indices || indices.length < 2) return false;
  const sorted = [...indices].sort((a, b) => a - b);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] !== sorted[i - 1] + 1) return false;
  }
  return true;
}

export default function SceneTimeline({
  scenes,
  selectedSceneIndex,
  selectedSceneIndices,
  onSelectScene,
  onContextAction,
  clipboardHasCrop,
  canUndo,
  canRedo,
  videoRef,
  totalDuration,
}) {
  const [{ pages: { reframeEditor: text } }] = useLanguage();
  const ctxText = text.timeline.contextMenu;
  const [playing, setPlaying] = useState(false);
  const [menu, setMenu] = useState(null); // { x, y, sceneIndex, time }

  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const playheadRef = useRef(null);
  const playheadLblRef = useRef(null);
  const headerTimeRef = useRef(null);
  const scrubFillRef = useRef(null);
  const scrubKnobRef = useRef(null);
  const scrubBarRef = useRef(null);
  const scrubbingRef = useRef(false);
  const playingRef = useRef(false);
  const selectedIdxRef = useRef(selectedSceneIndex);
  const scenesRef = useRef(scenes);

  const dur = totalDuration || (scenes?.[scenes.length - 1]?.end_time ?? 60);
  const totalPx = Math.max(dur * TIMELINE_PX_PER_SEC, (scenes?.length || 1) * MIN_BLOCK_PX);

  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;
    const onPlay = () => { setPlaying(true); playingRef.current = true; };
    const onPause = () => { setPlaying(false); playingRef.current = false; };
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
    };
  }, [videoRef]);

  useEffect(() => { selectedIdxRef.current = selectedSceneIndex; }, [selectedSceneIndex]);
  useEffect(() => { scenesRef.current = scenes; }, [scenes]);

  // RAF: direct DOM update for playhead + auto-scroll (no React re-render per frame).
  // The scene selection (which drives the crop box) is done separately, frame-accurate.
  useEffect(() => {
    const video = videoRef?.current;
    if (!video || !dur || !totalPx) return;

    const hasRVFC = typeof video.requestVideoFrameCallback === "function";

    // Switch the active scene when the playhead crosses a cut. Driven by the EXACT
    // displayed-frame time (requestVideoFrameCallback → mediaTime) so the crop box
    // switches precisely on the cut instead of a frame or two off — the rAF/currentTime
    // desync read as "tiny jumps" of the crop box at every cut. Mirrors VerticalPreview.
    const selectSceneAt = (t) => {
      if (!playingRef.current || !scenesRef.current?.length) return;
      const activeIdx = scenesRef.current.findIndex(
        (s) => t >= (s.start_time ?? 0) && t < (s.end_time ?? Infinity)
      );
      if (activeIdx !== -1 && activeIdx !== selectedIdxRef.current) {
        selectedIdxRef.current = activeIdx;
        onSelectScene(activeIdx, "single");
      }
    };

    const tick = () => {
      const t = video.currentTime;
      const px = (t / dur) * totalPx;

      if (playheadRef.current) playheadRef.current.style.left = `${px}px`;
      if (playheadLblRef.current) playheadLblRef.current.textContent = formatTime(t);
      if (headerTimeRef.current) headerTimeRef.current.textContent = formatTime(t);

      const pct = Math.max(0, Math.min(100, (t / dur) * 100));
      if (scrubFillRef.current) scrubFillRef.current.style.width = `${pct}%`;
      if (scrubKnobRef.current) scrubKnobRef.current.style.left = `${pct}%`;

      const el = scrollRef.current;
      if (el && playingRef.current) {
        const vl = el.scrollLeft;
        const vw = el.clientWidth;
        const visualPx = px + TIMELINE_LEFT_PAD;
        if (visualPx > vl + vw - 80 || visualPx < vl) {
          el.scrollLeft = Math.max(0, visualPx - vw * 0.3);
        }
      }

      if (!hasRVFC) selectSceneAt(t); // fallback: select in rAF when rVFC unsupported

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    let vfcHandle = null;
    if (hasRVFC) {
      const onFrame = (_now, meta) => {
        selectSceneAt(meta.mediaTime);
        vfcHandle = video.requestVideoFrameCallback(onFrame);
      };
      vfcHandle = video.requestVideoFrameCallback(onFrame);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (vfcHandle != null && video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(vfcHandle);
    };
  }, [videoRef, dur, totalPx, onSelectScene]);

  const seekFromScrub = useCallback((clientX) => {
    const el = scrubBarRef.current;
    const v = videoRef?.current;
    if (!el || !v || !dur) return;
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    v.currentTime = pct * dur;
  }, [dur, videoRef]);

  useEffect(() => {
    const onMove = (e) => { if (scrubbingRef.current) seekFromScrub(e.clientX); };
    const onUp = () => { scrubbingRef.current = false; };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [seekFromScrub]);

  const handleScrubDown = useCallback((e) => {
    scrubbingRef.current = true;
    seekFromScrub(e.clientX);
  }, [seekFromScrub]);

  const togglePlay = useCallback(() => {
    const v = videoRef?.current;
    if (!v) return;
    v.paused ? v.play() : v.pause();
  }, [videoRef]);

  // Convert clientX inside the scroll container to a timeline timestamp.
  const timeFromClientX = useCallback((clientX) => {
    const el = scrollRef.current;
    if (!el || !dur) return 0;
    const rect = el.getBoundingClientRect();
    const px = clientX - rect.left + el.scrollLeft - TIMELINE_LEFT_PAD;
    return Math.max(0, Math.min(px / TIMELINE_PX_PER_SEC, dur));
  }, [dur]);

  const handleTimelineClick = useCallback((e) => {
    if (menu) return;
    const t = timeFromClientX(e.clientX);
    if (videoRef?.current) videoRef.current.currentTime = t;
  }, [menu, timeFromClientX, videoRef]);

  // Click a scene block: select + seek to scene start. Modifier keys drive
  // multi-selection (range with shift, toggle with ctrl/meta).
  const handleSceneClick = useCallback((e, i) => {
    e.stopPropagation();
    let mode = "single";
    if (e.shiftKey) mode = "shift";
    else if (e.ctrlKey || e.metaKey) mode = "toggle";
    onSelectScene(i, mode);
    if (mode === "single") {
      const scene = scenes[i];
      if (videoRef?.current && scene?.start_time != null) {
        videoRef.current.currentTime = scene.start_time;
      }
    }
  }, [onSelectScene, scenes, videoRef]);

  const handleSceneContextMenu = useCallback((e, i) => {
    e.preventDefault();
    e.stopPropagation();
    const t = timeFromClientX(e.clientX);
    // If the right-clicked scene isn't part of the active multi-selection,
    // narrow the selection to just that scene so menu actions are unambiguous.
    const inSelection = selectedSceneIndices?.has?.(i);
    if (!inSelection) onSelectScene(i, "single");
    setMenu({ x: e.clientX, y: e.clientY, sceneIndex: i, time: t });
  }, [onSelectScene, selectedSceneIndices, timeFromClientX]);

  const closeMenu = useCallback(() => setMenu(null), []);

  const dispatchAction = useCallback((action) => {
    if (!menu) return;
    onContextAction(action, { sceneIndex: menu.sceneIndex, time: menu.time });
    closeMenu();
  }, [menu, onContextAction, closeMenu]);

  const menuScene = menu ? scenes[menu.sceneIndex] : null;
  const multiSize = selectedSceneIndices?.size ?? 0;
  const menuInsideMulti = menu && selectedSceneIndices?.has?.(menu.sceneIndex) && multiSize > 1;
  const selectionContiguous = useMemo(
    () => menuInsideMulti && isContiguous(Array.from(selectedSceneIndices)),
    [menuInsideMulti, selectedSceneIndices],
  );

  const menuInsideScene = menu && menuScene
    && menu.time > (menuScene.start_time ?? 0)
    && menu.time < (menuScene.end_time ?? Infinity);

  if (!scenes || !scenes.length) {
    return (
      <TimelineArea>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", p: 2 }}>
          {text.timeline.noScenes}
        </Typography>
      </TimelineArea>
    );
  }

  const rulerTicks = buildRulerTicks(dur);

  return (
    <TimelineArea>
      <TimelineHeader>
        <IconButton size="small" onClick={togglePlay} sx={{ color: "rgba(255,255,255,0.85)", p: "2px" }}>
          {playing ? <PauseRounded sx={{ fontSize: 18 }} /> : <PlayArrowRounded sx={{ fontSize: 18 }} />}
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "baseline", gap: "3px" }}>
          <Typography
            ref={headerTimeRef}
            component="span"
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.68rem", fontFamily: "monospace", userSelect: "none", minWidth: 30 }}
          >
            0:00
          </Typography>
          <Typography component="span" variant="caption" sx={{ color: "rgba(255,255,255,0.22)", fontSize: "0.6rem", fontFamily: "monospace" }}>
            / {formatTime(dur)}
          </Typography>
        </Box>

        <ScrubBar ref={scrubBarRef} onMouseDown={handleScrubDown}>
          <ScrubFill ref={scrubFillRef} style={{ width: 0 }} />
          <ScrubKnob ref={scrubKnobRef} style={{ left: 0 }} />
        </ScrubBar>

        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.28)", fontSize: "0.6rem", letterSpacing: "0.06em", fontWeight: 600, textTransform: "uppercase", flexShrink: 0 }}>
          {scenes.length} {text.timeline.sceneCount}
        </Typography>
      </TimelineHeader>

      <TimelineScroll ref={scrollRef} onClick={handleTimelineClick}>
        <TimelineInner style={{ width: totalPx + 48 }}>

          <TimeRuler>
            {rulerTicks.flatMap(({ t, px, major }) => {
              const els = [
                <RulerTick key={`t${t}`} style={{ left: px, height: major ? 12 : 5 }} />,
              ];
              if (major && t > 0) {
                els.push(
                  <RulerLabel key={`l${t}`} style={{ left: px }}>{formatTime(t)}</RulerLabel>
                );
              }
              return els;
            })}
          </TimeRuler>

          <SceneTrack>
            {scenes.map((scene, i) => {
              const sceneDur = (scene.end_time ?? dur) - (scene.start_time ?? 0);
              const blockLeft = (scene.start_time ?? 0) * TIMELINE_PX_PER_SEC;
              const blockW = Math.max(MIN_BLOCK_PX, sceneDur * TIMELINE_PX_PER_SEC) - 2;
              const color = STRATEGY_COLORS[scene.strategy] || STRATEGY_COLORS.undefined;
              const colorDark = STRATEGY_COLORS_DARK[scene.strategy] || STRATEGY_COLORS_DARK.undefined;
              const isPrimary = selectedSceneIndex === i;
              const isMulti = selectedSceneIndices?.has?.(i);
              const classes = [
                isPrimary ? "selected" : "",
                isMulti && multiSize > 1 ? "multi-selected" : "",
              ].filter(Boolean).join(" ");

              return (
                <SceneBlock
                  key={i}
                  className={classes}
                  style={{
                    left: blockLeft,
                    width: blockW,
                    background: `linear-gradient(175deg, ${color} 0%, ${colorDark} 100%)`,
                  }}
                  onClick={(e) => handleSceneClick(e, i)}
                  onContextMenu={(e) => handleSceneContextMenu(e, i)}
                  title={`${scene.strategy || "undefined"} | ${formatTime(scene.start_time)}–${formatTime(scene.end_time)}`}
                >
                  <SceneBlockLabel>
                    {scene.strategy?.replace("_", " ") || "?"}
                  </SceneBlockLabel>
                  {blockW > 88 && (
                    <SceneBlockTime>
                      {formatTime(scene.start_time)}–{formatTime(scene.end_time)}
                    </SceneBlockTime>
                  )}
                </SceneBlock>
              );
            })}
          </SceneTrack>

          <PlayheadLine ref={playheadRef}>
            <PlayheadTimeLabel ref={playheadLblRef}>0:00</PlayheadTimeLabel>
          </PlayheadLine>

        </TimelineInner>
      </TimelineScroll>

      <Menu
        open={!!menu}
        onClose={closeMenu}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
        autoFocus={false}
        disableAutoFocusItem
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "#1a1a24",
              color: "rgba(255,255,255,0.9)",
              border: "1px solid rgba(255,255,255,0.08)",
              minWidth: 240,
              "& .MuiMenuItem-root": {
                fontSize: "0.78rem",
                "&:hover": { backgroundColor: "rgba(155,70,225,0.18)", color: "#fff" },
                "&.Mui-focusVisible": { backgroundColor: "rgba(155,70,225,0.18)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.35)", opacity: 1 },
              },
              "& .MuiListItemIcon-root": { color: "rgba(255,255,255,0.6)", minWidth: 28 },
              "& .MuiDivider-root": { borderColor: "rgba(255,255,255,0.08)" },
            },
          },
        }}
      >
        <MenuItem onClick={() => dispatchAction("split")} disabled={!menuInsideScene}>
          <ListItemIcon><ContentCutRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{ctxText.splitHere}</ListItemText>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", ml: 2 }}>{ctxText.shortcut.split}</Typography>
        </MenuItem>

        {menuInsideMulti ? (
          <MenuItem onClick={() => dispatchAction("merge_selected")} disabled={!selectionContiguous}>
            <ListItemIcon><MergeTypeRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{ctxText.mergeSelected.replace("{n}", multiSize)}</ListItemText>
          </MenuItem>
        ) : (
          <>
            <MenuItem onClick={() => dispatchAction("merge_prev")} disabled={!menu || menu.sceneIndex <= 0}>
              <ListItemIcon><MergeTypeRoundedIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} /></ListItemIcon>
              <ListItemText>{ctxText.mergePrev}</ListItemText>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", ml: 2 }}>{ctxText.shortcut.mergePrev}</Typography>
            </MenuItem>
            <MenuItem onClick={() => dispatchAction("merge_next")} disabled={!menu || menu.sceneIndex >= scenes.length - 1}>
              <ListItemIcon><MergeTypeRoundedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{ctxText.mergeNext}</ListItemText>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", ml: 2 }}>{ctxText.shortcut.mergeNext}</Typography>
            </MenuItem>
          </>
        )}

        <Divider />

        <MenuItem onClick={() => dispatchAction("copy_crop")}>
          <ListItemIcon><ContentCopyRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{ctxText.copyCrop}</ListItemText>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", ml: 2 }}>{ctxText.shortcut.copy}</Typography>
        </MenuItem>
        <MenuItem onClick={() => dispatchAction("paste_crop")} disabled={!clipboardHasCrop}>
          <ListItemIcon><ContentPasteRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>
            {menuInsideMulti
              ? ctxText.pasteCropMulti.replace("{n}", multiSize)
              : ctxText.pasteCrop}
          </ListItemText>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", ml: 2 }}>{ctxText.shortcut.paste}</Typography>
        </MenuItem>
        <MenuItem onClick={() => dispatchAction("apply_to_all")}>
          <ListItemIcon><LayersRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{ctxText.applyCropToAll}</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => dispatchAction("reset")}>
          <ListItemIcon><RestartAltRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{ctxText.resetScene}</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => dispatchAction("undo")} disabled={!canUndo}>
          <ListItemIcon><UndoRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{ctxText.undo}</ListItemText>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", ml: 2 }}>{ctxText.shortcut.undo}</Typography>
        </MenuItem>
        <MenuItem onClick={() => dispatchAction("redo")} disabled={!canRedo}>
          <ListItemIcon><RedoRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{ctxText.redo}</ListItemText>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", ml: 2 }}>{ctxText.shortcut.redo}</Typography>
        </MenuItem>
      </Menu>
    </TimelineArea>
  );
}
